import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import GoogleMapReact from 'google-map-react';
import { findBounds } from '../../../lib/map';
import styled, { css } from 'styled-components'
import { Icon } from '@iconify/react';
import { PositionStatusBox, StatusContainer } from './Statuses';
import { SiteStatusBox } from './SiteStatuses';
import { Settings, SettingsOpen } from './Settings'
import settingsSolid from '@iconify/icons-clarity/settings-solid';
// import helpIcon from '@iconify/icons-entypo/help';
import FadeLoader from 'react-spinners/FadeLoader';
// import { PositionMapMarker } from './PositionMapMarker';
// import { SiteMarker } from './SiteMarker';
import { fetchPositionSamples, fetchPositionTimePeriods, togglePositionStatusBoxDisplay, toggleSelected } from '../../../store/positions/positionsSlice';
import { toggleSiteStatusBoxDisplay } from '../../../store/positions/sitesSlice';
import { updateZoom, resetZoom } from '../../../store/devices/mapSettingsSlice';
// import { debounce } from 'lodash';
import { getSelectedNodes, selectAllOfflineNodePositions, selectAllOfflineNodeSites, selectAllOnlineNodePositions, selectAllOnlineNodeSites } from '../../../store/positions/menuNodesSlice';
// import { colour_lookup } from '../../common/Colours';
import useSupercluster from 'use-supercluster';
import { SiteClusterMarker } from './SiteClusterMarker';
import { PositionMapClusterMarker } from './PositionMapClusterMarker';
import { getFilterStatus } from '../../../store/positions/filterStatusSlice';

/* --- Map component container css --- */
const StyledMap = styled.div`
  height: 100vh;
  width: 100%;
  position: relative;
`;

// checks if a single site is in the current map bounds
// used for toggling between site and position view if only one site is visible
const checkSingleSiteInView = (sites: any, bounds: any) => {
  
  let ne = bounds.ne;
  let sw = bounds.sw;
  let site_count = 0;
  
  sites.forEach(site => {
    let check_lat = site.coordinates.lat > sw.lat && site.coordinates.lat < ne.lat;
    let check_lng = site.coordinates.lng > sw.lng && site.coordinates.lng < ne.lng;
    
    if (check_lat && check_lng) {
      site_count++;
    }
  });
  
  return site_count <= 1? true : false;
}

// log maps to see all options
const createMapOptions = (maps) => {
  return {
    scrollwheel: true,
    fullscreenControl: true,
    streetViewControl: false,    
    disableDoubleClickZoom: true,
    mapTypeControl: true,
        mapTypeControlOptions: {
            style: maps.MapTypeControlStyle.HORIZONTAL_BAR,
            position: maps.ControlPosition.BOTTOM_CENTER,
            mapTypeIds: [
                maps.MapTypeId.ROADMAP,
                maps.MapTypeId.SATELLITE
            ]
        },
    zoomControl: true,
    zoomControlOptions: {
      position: maps.ControlPosition.LEFT_BOTTOM,
      style: maps.ZoomControlStyle.SMALL
    }
  } 
}

// const createMiniMapOptions = (maps) => {
//   return {
//     scrollwheel: false,
//     fullscreenControl: false,
//     zoomControl: false,
//     zoomControlOptions: {
//       position: maps.ControlPosition.BOTTOM_CENTER
//     }
//   }
// }

const LoadingSplash = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  background: rgb(255,255,255,0.7);
  z-index: 1;
`;

const spinner = css`
  position: absolute;
  top: 50%;
  left: 50%;
`;

// const StyledHover = styled.div<{ position: { x: number; y: number; } }>`
//   z-index: 9999;
//   width: 400px;
//   height: 400px;
//   // border-radius: 50px;
//   border: 10px solid #FFF;
//   box-shadow: 1px 1px 10px rgba(0, 0, 0, 0.25);
//   display: block;
//   cursor: pointer;
//   position: fixed;
//   top: ${props => props.position.y + 5}px;
//   left: ${props => props.position.x + 5}px;
//   pointer-events: none;
// `;

// const StyledInnerHover = styled.div`
//   z-index: 9999;
//   width: 380px;
//   height: 380px;
//   // box-shadow: 1px 1px 10px rgba(0, 0, 0, 0.25);
//   display: block;
//   cursor: pointer;
//   pointer-events: auto;
// `;

// const optionsCursorTrueWithMargin = {
//   followCursor: false,
//   shiftX: 0,
//   shiftY: 0
// };

/* ------ Reset Button component start------ */
const StyledResetZoom =  styled.div`
  width: 200px;
  height: 50px;
  background: rgba(255,255,255,0.9);
  border-radius: 25px;
  position: absolute;
  left: 50%;
  transform: translate(-50%, 0);
  top: 10px;
  padding: 5px;
  cursor: pointer;
  color: #555;
  box-shadow: 1px 1px 10px rgba(0, 0, 0, 0.25);

  &:hover {
    color: #333;
    border: 1px solid #ccc;
  }
`;

const ResetZoomText = styled.div`
  font-size: 16px;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

const ResetZoom = ({ mapSettings }: { mapSettings?: any }) => {
  
  const dispatch = useDispatch();
  
  const zoom_equal = mapSettings.zoom === mapSettings.selection_zoom;
  let center_equal = false;
  if (mapSettings.selection_center.lat !== null) {
    center_equal = mapSettings.center.lat.toFixed(3) === mapSettings.selection_center.lat.toFixed(3) && mapSettings.center.lng.toFixed(3) === mapSettings.selection_center.lng.toFixed(3);
  }
  
  return (
    (!zoom_equal || !center_equal) &&
      <StyledResetZoom onClick={() => { dispatch(resetZoom()) }}>
        <ResetZoomText>Reset Zoom</ResetZoomText>
      </StyledResetZoom>
  )
}
/* ------ Reset Button component end------ */

/* ------ Map component start ------ */
export const Map = ({ mapSettings, positions, sites, features, fullscreen }: { mapSettings: any; positions: any; sites: any, features: any; fullscreen: boolean }) => {
  const mapRef = useRef();
  const dispatch = useDispatch();

  // Get current filter status
  const currentStatus = useSelector(getFilterStatus);
  const onlineSites = useSelector(selectAllOnlineNodeSites);
  const offlineSites = useSelector(selectAllOfflineNodeSites);
  const onlinePositions = useSelector(selectAllOnlineNodePositions);
  const offlinePositions = useSelector(selectAllOfflineNodePositions);

  // initialise selected nodes consts
  const selectedNode = useSelector(getSelectedNodes);
  const selectedNodeArray = selectedNode;

  // If currentStatus is all, filteredPositions are all positions. Otherwise filter.
  const filteredPositions = currentStatus == "all"? positions : positions.filter((obj1) => {
    // Status to filter is either online or offline
    const statusToFilter = currentStatus == "online"? onlinePositions : offlinePositions;
    return statusToFilter.some((obj2) => {
    return obj2.label === obj1.title && parseInt(obj2.id, 10) === obj1.id;
      });
  });
  // If currentStatus is all, filteredSites are all sites. Otherwise filter.
  const filteredSites = currentStatus == "all"? sites : sites.filter((obj1) =>{
    const statusToFilter = currentStatus == "online"? onlineSites : offlineSites;
    return statusToFilter.some((obj2) => {
      return obj2.label === obj1.title && obj2.id === obj1.id;
        });
  })
 
  // create array consts for positions and sites
  const positionsArray = filteredPositions;
  const sitesArray = filteredSites;

  // filter positions and sites to create new arrays that match selected nodes
  const matchingPositions = positionsArray.filter((obj1) => {
    return selectedNodeArray.some((obj2) => {
    return obj2.label === obj1.title && parseInt(obj2.id, 10) === obj1.id;
      });
  });
  const matchingSites = sitesArray.filter((obj1) => {
    return selectedNodeArray.some((obj2) => {
    return obj2.label === obj1.title && obj2.id === obj1.id;
      });
    });

  // linked_sites are all existing sites with their related positions attached in an array
  let linked_sites = [];
  if (sitesArray.length > 0) {
    linked_sites = sitesArray.map((site, index) => {
      let site_positions = positions.filter((position) => position.site_id === site.id)
      return {
        ...site,
        positions: site_positions,
        coordinates: findBounds(site_positions, 640, 380, 5).center,
        //mini_map_bounds: findBounds(site_positions, 300, 300, 5)
      }
    })
  }

  // matching linked_sites are all selected sites with their related positions attached in an array
  let matching_linked_sites = [];
  if (matchingSites.length > 0) {
    matching_linked_sites = matchingSites.map((site, index) => {
      let site_positions = positions.filter((position) => position.site_id === site.id)
      return {
        ...site,
        positions: site_positions,
        coordinates: findBounds(site_positions, 640, 380, 5).center,
        //mini_map_bounds: findBounds(site_positions, 300, 300, 5)
      }
    })
  }

  // determines settings for the key modal
  const [settings, setSettings] = useState({
    display: false,
    editable: false,
    features: {
      connectivity: true,
      exceedances: true,
      battery: true,
      tunnel: false,
    }
  });
  
  const [singleSite, setSingleSite] = useState(false);
  // const [siteHover, setSiteHover] = useState({ active: false, site: null, mouse_position: null });

  /* --- position and site status modal toggles --- */
  const togglePositionStatus = (position) => {
    dispatch(togglePositionStatusBoxDisplay(position.station_id));    
  }
  
  const toggleSiteStatus = (site) => {
    dispatch(toggleSiteStatusBoxDisplay(site.id))
  }
  
  // const miniMapOnClick = (site) => {
  //   return () => {
  //     dispatch(updateZoom(findBounds(site.positions, 700, 500, 5)));
  //     setSiteHover({ active: false, site: null, mouse_position: null });
  //   }
  // }

  // Zoom clicked site into view
  // const siteZoom = (site) => {
  //   return () => {
  //     dispatch(updateZoom(findBounds(site.positions, 700, 500, 5)));
  //   }
  // }

  // Zoom clicked position into view - not in use at the moment
  const positionZoom = function(position) {
    const positions = [position];
    return () => {
      // toggles status box, sets selected to true for that position
      // fetches samples and timeperiods for that position, then zooms into it
      togglePositionStatus(position);
      dispatch(toggleSelected({id: position.id}))
      dispatch(fetchPositionSamples(position));
      dispatch(fetchPositionTimePeriods(positions[0]));
      dispatch(updateZoom(findBounds(positions, 700, 500, 16)));
    }
  }

  const mapOnChange = (center, zoom, bounds) => {
    dispatch(updateZoom({zoom, center}))
    setBounds([
      bounds.nw.lng,
      bounds.se.lat,
      bounds.se.lng,
      bounds.nw.lat
    ]);
    setZoom(zoom);
    // this was stopping the marker clustering from working properly, I think at this point maybe we replace sites with marker clustering for the positions
    // then rely on the side menu to filter the sites. Alternatively, have two separate superclusters and alter the singleSiteInView script to use a single cluster
    if (selectedNode.length === 0 ? linked_sites !== null && linked_sites.length > 0 : matching_linked_sites !== null && matching_linked_sites.length >= 0) {
      setSingleSite(checkSingleSiteInView(selectedNode.length === 0 ? linked_sites : matching_linked_sites, bounds));
    }
  }

  // if the number of selected items changes, zoom to the center of the newly selected items
  const [selectedNodeItems, setSelectedNodeItems] = useState(0);
  if(selectedNodeItems !== selectedNode.length){
    const previousValue = selectedNodeItems;
    const currentValue = selectedNode.length;

    setSelectedNodeItems(selectedNode.length);

    // filter out a list of selected positions as an array for the findBounds function
    const filteredStations = selectedNodeArray.filter((obj) => {
      return obj.type === 'station';
    });
    const filteredPositionStations = positionsArray.filter((obj1) => {
      return filteredStations.some((obj2) => {
      return obj2.label === obj1.title && parseInt(obj2.id, 10) === obj1.id;
        });
    });

    // if everything is deselected, reset zoom to default
    if(previousValue > 0 && currentValue === 0){
      dispatch(resetZoom());
    } else {
      dispatch(updateZoom(findBounds(filteredPositionStations, 700, 500, 17)));
    }
  }


  /* --- Minimap hover calculations and anchoring start --- */
  // // https://stackoverflow.com/questions/54705254/how-to-keep-showing-the-popover-on-hovering-on-the-anchorel-and-popover-as-w
  // const calculateAndSetHover = (active, site, e) => {
  //   if (e !== null && typeof e.target !== 'undefined') {
  //     let icon = e.target.getBoundingClientRect();
      
  //     let total_width = window.innerWidth;
  //     let total_height = window.innerHeight;
  //     let map_width = total_width * 0.8;
  //     let sidebar_width = total_width * 0.2;
  //     let center = { x: map_width/2, y: total_height/2 };
  //     let mouse = { x: e.pageX - sidebar_width, y: e.pageY };
  //     let icon_center = { x: ((icon.right + icon.left) / 2) - sidebar_width, y: (icon.bottom + icon.top) / 2 }
      
  //     let quadrant = null;
  //     let mini_map = { x: null, y: null };
      
  //     if (icon_center.x >= center.x) {
  //       mini_map.x = icon.left - 400;
  //     } else {
  //       mini_map.x = icon.right;
  //     }
      
  //     if (icon_center.y >= center.y) {
  //       mini_map.y = icon.top - 400;
  //     } else {
  //       mini_map.y = icon.bottom;
  //     }

  //     debouncedSetSiteHover(active, site, mini_map)
  //   } else {
  //     debouncedSetSiteHover(active, site, e)
  //   }
  // }
  
  // const rawSetSiteHover = (active, site, e) => {
    
  //   let new_site_hover: any = {
  //     active: active,
  //     site: site
  //   }
    
  //   if (e === null) {
  //     new_site_hover.mouse_position = null;
  //   } else {
  //     new_site_hover.mouse_position = {
  //       x: e.x,
  //       y: e.y
  //     }
  //   }
  //   setSiteHover(new_site_hover)
  // }
  
  // const debouncedSetSiteHover = useCallback(debounce(rawSetSiteHover, 200), [])
  /* --- Minimap hover calculations and anchoring end --- */

  /* ------ Marker Clustering start ------ */
  // this section is key for setting out the clustering mechanism using the 'use-supercluster' library
  // Dynamic markers based on whether any positions have been selected in the PowerSingleSelectPositionsMenu
  const positionMarkers = selectedNode.length === 0 ? positionsArray : matchingPositions; 
  const siteMarkers = selectedNode.length === 0 ? linked_sites : matching_linked_sites;

  // markers must be made using a geoJSON format that contains a type, properties and geometry
  // the geometry must have a type of point and lat + lng coordinates
  // the properties section allows for extra interactability
  const siteClusterMarkerPoints = siteMarkers.map(linkedSiteMarker => ({
    type: "Feature",
    properties: {cluster: false, clusterId: linkedSiteMarker.id, category: "site", title: linkedSiteMarker.title, positions: linkedSiteMarker.positions.length, positionsInfo: linkedSiteMarker.positions},
    geometry: {
      type: "Point",
      coordinates: [
        parseFloat(linkedSiteMarker.coordinates.lng),
        parseFloat(linkedSiteMarker.coordinates.lat)
      ]
    }
  }));
  const positionClusterMarkerPoints = positionMarkers.map(positionMarker => ({
    type: "Feature",
    properties: {cluster: false, clusterId: positionMarker.id, category: "position", title: positionMarker.title, index: 0},
    geometry: {
      type: "Point",
      coordinates: [
        parseFloat(positionMarker.coordinates.longitude),
        parseFloat(positionMarker.coordinates.latitude)
      ]
    }
  }));

  const [bounds, setBounds] = useState(null);
  const [zoom, setZoom] = useState(20);

  const {clusters: siteClusterMarkers, supercluster: superSiteClusterMarkers } = useSupercluster({
    points: siteClusterMarkerPoints,
    bounds: bounds,
    zoom: zoom,
    options: {radius: 100, maxZoom: 16, minZoom: 0, minPoints: 2}
  });

  const {clusters: positionClusterMarkers, supercluster: superPositionClusterMarkers } = useSupercluster({
    points: positionClusterMarkerPoints,
    bounds: bounds,
    zoom: zoom,
    options: {radius: 120, maxZoom: 22, minZoom: 0, minPoints: 2}
  });
  /* ------ Marker Clustering end ------ */

  return (
    <StyledMap>
      {positions.length === 0 &&
        <LoadingSplash>
          <FadeLoader
          css={`${spinner}`}
          color={"#777"}
          loading={true}
          width={4}
          />
        </LoadingSplash>
      }
      <GoogleMapReact
        // remember to change api key back
        // development = AIzaSyCHaQOQqWga7_33hgw7M61hoROOHDy9AJ0
        // production = AIzaSyC7vXlEcrmkK95WmI93Hr2uWLRwkjfhobU
        bootstrapURLKeys = {{ key: 'AIzaSyC7vXlEcrmkK95WmI93Hr2uWLRwkjfhobU' }}
        defaultCenter    = {{ lat: 51.501280, lng: -2.477420 }}
        center           = {mapSettings.center}
        zoom             = {mapSettings.zoom}
        options          = {createMapOptions}
        onChange         = {({center, zoom, bounds}) => { mapOnChange(center, zoom, bounds); }}
        yesIWantToUseGoogleMapApiInternals
        onGoogleApiLoaded={({ map }) => { mapRef.current = map; }}
      >
        {/* ------ Site Marker Clustering ----- */}
        {mapSettings.zoom < 15 && siteClusterMarkers && linked_sites != null && !singleSite? siteClusterMarkers.map((cluster, index) => {
          const longitude = cluster.geometry.coordinates[0];
          const latitude = cluster.geometry.coordinates[1];
          const {
            cluster: isCluster,
            point_count: pointCount
          } = cluster.properties;

          if (isCluster){
            return (
                <SiteClusterMarker 
                key={index} 
                lat={latitude} 
                lng={longitude} 
                pointCount={pointCount}
                displayText={false}
                label={cluster.properties.title}
                cluster={cluster}
                onClick = {(cluster) => {
                  const expansionZoom = Math.min(
                    superSiteClusterMarkers.getClusterExpansionZoom(cluster.id),
                    20
                  );  
                  const zoom = expansionZoom;
                  const center = {
                    lat: latitude,
                    lng: longitude
                  }
                  dispatch(updateZoom({zoom, center}))
                }}
                />
            );
          } else {
            if(pointCount === undefined){
              const displayText = true;
              const positions = cluster.properties.positions;
              return(
                <SiteClusterMarker 
                key={index} 
                lat={latitude} 
                lng={longitude} 
                pointCount={pointCount === undefined? positions : pointCount}
                displayText={displayText}
                label={cluster.properties.title}
                cluster={cluster}
                onClick = {(cluster) => {
                  const zoom = 15;
                  const center = {
                    lat: latitude,
                    lng: longitude
                  }
                  dispatch(updateZoom({zoom, center}))
                }}
                />
              )
            }
            return null;
          }
        }) : null}

        {/* ------ Position Marker Clustering ------ */}
        {(mapSettings.zoom >= 15 || (sites.length <= 1 && positionClusterMarkers.length > 0) || (singleSite && positionClusterMarkers.length > 0)) && positionClusterMarkers.map((cluster, index) => {
          const longitude = cluster.geometry.coordinates[0];
          const latitude = cluster.geometry.coordinates[1];
          const id = cluster.properties.clusterId
          const {
            cluster: isCluster,
            point_count: pointCount
          } = cluster.properties;
          const positionArray = positions.filter((obj) => {
            return obj.id === id
          });
          const position = positionArray[0];
          const zoomLevel = mapSettings.zoom;

          if(isCluster){
            // console.log("cluster", cluster);
            // console.log("cluster leaves", superPositionClusterMarkers.getLeaves(cluster.id));;
            // If position is a cluster and has children
            if (mapSettings.zoom < 18){
                return(
                  <PositionMapClusterMarker
                  key={index} 
                  lat={latitude} 
                  lng={longitude} 
                  pointCount={pointCount} 
                  position={position}
                  settings={settings}
                  label={cluster.properties.title}
                  cluster={cluster}
                  displayData={false}
                  childClusters={null}
                  onClick = {(cluster) => {
                    const expansionZoom = Math.min(
                      superPositionClusterMarkers.getClusterExpansionZoom(cluster.id),
                      22
                    );  
                    const zoom = expansionZoom;
                    const center = {
                      lat: latitude,
                      lng: longitude
                    }
                    dispatch(updateZoom({zoom, center}))
                  }}
                  />
                )
            } else if (mapSettings.zoom >= 15){
                const childClusters = superPositionClusterMarkers.getChildren(cluster.id);
                const positionsToCluster = positionsArray.filter((obj1) => {
                  return childClusters.some((obj2) => {
                  return obj2.properties.clusterId === obj1.id;
                    });
                });
                return(
                    <PositionMapClusterMarker
                    key={index} 
                    lat={latitude} 
                    lng={longitude} 
                    pointCount={pointCount} 
                    position={position}
                    settings={settings}
                    label={cluster.properties.title}
                    cluster={cluster}
                    displayData={true}
                    childClusters={positionsToCluster}
                    onClick = {null}                    
                    />
                )
            }
            return null;
          } else {
            if(pointCount === undefined){
              return(
                <PositionMapClusterMarker
                key={index} 
                lat={latitude} 
                lng={longitude} 
                pointCount={pointCount} 
                position={position}
                settings={settings}
                label={cluster.properties.title}
                cluster={cluster}
                displayData={true}
                childClusters={null}
                onClick = {(cluster) => {
                  // dispatch(updateZoom({zoom, center}))
                  togglePositionStatus(position);
                  dispatch(toggleSelected({id: position.id}))
                }}    
                />
              )
            }
            return null;
          }
        })}
      </GoogleMapReact>
            
      <StatusContainer>
        {positions.map((position, index) => (
          position.status_box_display && 
          <PositionStatusBox key={index} onClose={togglePositionStatus} position={position}/>
        ))}
        {linked_sites.map((site, index) => (
          site.status_box_display &&
          <SiteStatusBox key={index} onClose={toggleSiteStatus} settings={settings} site={site}/>
        ))}
      </StatusContainer>
      <Settings settings={settings} setSettings={setSettings} features={features} fullscreen={fullscreen}/>
      <SettingsOpen features={features} fullscreen={fullscreen} onClick={() => {setSettings({ ...settings, display: !settings.display })}}>
        {settings.editable? <Icon width='40px' height='40px' icon={settingsSolid}/> : <div>Key</div>}
      </SettingsOpen>
      {positions.length !== 0 &&
      <ResetZoom mapSettings={mapSettings}/>}
    </StyledMap>
  );
}
/* ------ Map component end ------ */